import React from 'react';
import { bandFor, POWER_SCORE_BANDS } from '../utils/powerScoreBands';

// "95–100" for the top band, "< 50" for the bottom (min === 0) band, else
// "min–max" — mirrors the range formatting used in the iOS explainer sheet.
function bandRangeLabel(band) {
  if (band.min === 0) return `< ${band.max + 1}`;
  return `${band.min}–${band.max}`;
}

const SUBSCORES = [
  { key: 'speed',        label: 'Speed' },
  { key: 'explosion',    label: 'Explosion' },
  { key: 'agility',      label: 'Agility' },
  { key: 'size',         label: 'Size' },
  { key: 'verification', label: 'Verification' },
];

function SubscoreBar({ label, value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
      <div style={{ width: 96, color: '#475569' }}>{label}</div>
      <div style={{ flex: 1, background: '#e2e8f0', borderRadius: 999, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: '#0066cc', height: '100%', borderRadius: 999 }} />
      </div>
      <div style={{ width: 28, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{value ?? '—'}</div>
    </div>
  );
}

/**
 * Unified card for the ProZpects Power Score (v1).
 *
 * Power Score is a Free-tier feature (spec section 2) — every viewer with
 * access to this card at all sees the real score, never a paywall. It used
 * to carry a `locked` prop for an upgrade-CTA state, but no caller ever set
 * it (Power Score was never gated), so that branch and its
 * `data-testid="power-score-locked"` hook were removed as dead code. Do not
 * reintroduce a locked state derived from `base_user` on the wire — that
 * role only means "no subscription row," never "not entitled." If Power
 * Score is ever gated for real, it should read `useEntitlements().has(...)`,
 * the same as every other paywalled feature in this codebase.
 *
 * Two states:
 *   1. score present → headline number + sub-score bars
 *   2. score === null → "Complete your measurables to unlock"
 *
 * Props:
 *   score        — { score, speed, explosion, agility, size, verification, version, computed_at } | null
 *   compact      — boolean: shrink for sticky-header / search row contexts
 *   missing      — array of field names missing for this athlete (drives the "needs X" line)
 */
export default function PowerScoreCard({ score, compact = false, missing = [] }) {
  // ── No score yet (athlete view, incomplete data) ───────────────
  if (!score) {
    return (
      <div className="power-score-card power-score-card--incomplete">
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, color: '#0066cc', textTransform: 'uppercase' }}>
          ProZpects Power Score
        </div>
        <div style={{ marginTop: 8, fontSize: 32, fontWeight: 800, color: '#cbd5e1', lineHeight: 1 }}>—</div>
        <div style={{ marginTop: 6, fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>
          Complete your measurables to unlock your Power Score.
          {missing.length > 0 && (
            <> Missing: <span style={{ color: '#0f172a', fontWeight: 600 }}>{missing.join(', ')}</span>.</>
          )}
        </div>
      </div>
    );
  }

  // ── Full score ────────────────────────────────────────────────
  const band = bandFor(score.score);

  if (compact) {
    return (
      <div className="power-score-card power-score-card--compact" style={{ background: band?.bg || '#f8fafc' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: band?.color || '#475569', textTransform: 'uppercase' }}>
          Power Score
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: band?.color || '#0f172a', lineHeight: 1 }}>{score.score}</div>
          <div style={{ fontSize: 12, color: band?.color || '#475569', fontWeight: 600 }}>{band?.label}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="power-score-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, color: '#0066cc', textTransform: 'uppercase' }}>
            ProZpects Power Score
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, color: band?.color || '#0f172a', lineHeight: 1, marginTop: 4 }}>
            {score.score}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: band?.color || '#475569', marginTop: 4 }}>
            {band?.label}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {SUBSCORES.map(s => (
          <SubscoreBar key={s.key} label={s.label} value={score[s.key]} />
        ))}
      </div>

      <details className="ps-explainer">
        <summary>How this is calculated</summary>
        <ul>
          <li><strong>30% Speed</strong> — 40-yard dash + 10-yard split</li>
          <li><strong>25% Explosion</strong> — vertical jump + broad jump</li>
          <li><strong>20% Agility</strong> — short shuttle + 3-cone drill</li>
          <li><strong>15% Size</strong> — height + weight vs. typical recruit frame</li>
          <li><strong>10% Verification</strong> — share of inputs confirmed at a camp or by staff</li>
        </ul>
        <p>
          Bands: {POWER_SCORE_BANDS.map((b, i) => (
            <React.Fragment key={b.label}>
              {i > 0 && ' · '}
              {bandRangeLabel(b)} {b.label}
            </React.Fragment>
          ))}. Same formula for every athlete.
        </p>
      </details>

      <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
        v{score.version}
        {score.computed_at && ` · updated ${new Date(score.computed_at).toLocaleDateString()}`}
      </div>
    </div>
  );
}
